const AUTH_API_BASE = 'https://auth-api.arkturian.com/api/v1/auth';
const CANONICAL_CLIENT_ID = '3d-presenter-web';
const CANONICAL_REDIRECT_URIS = new Set([
  'https://presenter.arkturian.com/auth/callback',
  'http://localhost:9002/auth/callback',
]);
const CLIENT_STORAGE_KEY = '3dp.oauth.client-id.v1';
const ACCESS_TOKEN_KEY = '3dp.oauth.access-token.v1';
const REFRESH_TOKEN_KEY = '3dp.oauth.refresh-token.v1';
const PKCE_VERIFIER_KEY = '3dp.oauth.pkce-verifier.v1';
const OAUTH_STATE_KEY = '3dp.oauth.state.v1';

export interface PresenterRoleClaim {
  sys: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer' | string;
  tid: string;
}

export interface AuthUser {
  id: string;
  email: string;
  email_verified?: boolean;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  picture?: string | null;
  roles?: PresenterRoleClaim[];
  tenants?: AuthTenant[];
}

export interface AuthTenant {
  id: string;
  display_name: string;
  description?: string | null;
  is_active: boolean;
  my_role?: 'owner' | 'admin' | 'editor' | 'viewer';
}

export interface AuthTenantMember {
  user_id: string;
  email: string;
  name: string;
  role: string;
  system?: string;
  expires_at?: string | null;
}

interface OAuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomValue(length = 48): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function sha256(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return base64Url(new Uint8Array(digest));
}

function getRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      detail = body.detail || body.error_description || body.error || detail;
    } catch {
      // Keep the HTTP status as the useful fallback.
    }
    throw new Error(String(detail));
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export class AuthApiClient {
  async signInWithEmail(email: string, password: string): Promise<void> {
    const response = await fetch(`${AUTH_API_BASE}/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim(), password }),
    });
    this.storeTokens(await readJson<OAuthTokenResponse>(response));
  }

  async registerWithEmail(input: {
    email: string; password: string; first_name: string; next_url: string;
  }): Promise<{ mailSent: boolean }> {
    const response = await fetch(`${AUTH_API_BASE}/signup/email`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const result = await readJson<OAuthTokenResponse & { verification?: { mail_sent?: boolean } }>(response);
    this.storeTokens(result);
    // A verification link must come from the mailbox, never the signup response.
    return { mailSent: result.verification?.mail_sent === true };
  }

  get accessToken(): string | null {
    return window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  private async ensureClientId(): Promise<string> {
    if (CANONICAL_REDIRECT_URIS.has(getRedirectUri())) return CANONICAL_CLIENT_ID;

    if (!import.meta.env.DEV) {
      throw new Error('Diese Domain ist nicht als sichere Anmelde-URL registriert.');
    }

    const cached = window.localStorage.getItem(CLIENT_STORAGE_KEY);
    if (cached) return cached;

    const response = await fetch(`${AUTH_API_BASE}/oauth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_name: '3D Presenter Web',
        redirect_uris: [getRedirectUri()],
        grant_types: ['authorization_code', 'refresh_token'],
        response_types: ['code'],
        token_endpoint_auth_method: 'none',
        scope: 'openid profile email',
      }),
    });
    const registered = await readJson<{ client_id: string }>(response);
    if (!registered.client_id) throw new Error('AuthAPI lieferte keine client_id.');
    window.localStorage.setItem(CLIENT_STORAGE_KEY, registered.client_id);
    return registered.client_id;
  }

  async beginLogin(returnTo = '/app'): Promise<void> {
    if (!window.isSecureContext || !crypto?.subtle) {
      throw new Error('Die sichere Anmeldung benötigt HTTPS.');
    }
    const clientId = await this.ensureClientId();
    const verifier = randomValue(64);
    const state = randomValue(32);
    window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    window.sessionStorage.setItem(OAUTH_STATE_KEY, JSON.stringify({ state, returnTo }));

    const query = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: getRedirectUri(),
      code_challenge: await sha256(verifier),
      code_challenge_method: 'S256',
      state,
      scope: 'openid profile email',
    });
    window.location.assign(`${AUTH_API_BASE}/oauth/authorize?${query.toString()}`);
  }

  async completeLogin(code: string, returnedState: string): Promise<string> {
    const storedStateRaw = window.sessionStorage.getItem(OAUTH_STATE_KEY);
    const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!storedStateRaw || !verifier) throw new Error('Anmeldezustand fehlt oder ist abgelaufen.');
    const stored = JSON.parse(storedStateRaw) as { state: string; returnTo: string };
    if (stored.state !== returnedState) throw new Error('Ungültiger OAuth-State.');

    const clientId = await this.ensureClientId();
    const form = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: getRedirectUri(),
      code_verifier: verifier,
    });
    const response = await fetch(`${AUTH_API_BASE}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: form,
    });
    const tokens = await readJson<OAuthTokenResponse>(response);
    this.storeTokens(tokens);
    window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    window.sessionStorage.removeItem(OAUTH_STATE_KEY);
    return safeReturnPath(stored.returnTo);
  }

  async getMe(): Promise<AuthUser> {
    return this.authorized<AuthUser>('/me');
  }

  async listTenants(): Promise<AuthTenant[]> {
    return this.authorized<AuthTenant[]>('/self-service/teams');
  }

  async listTenantMembers(tenantId: string): Promise<AuthTenantMember[]> {
    return this.authorized<AuthTenantMember[]>(`/self-service/teams/${encodeURIComponent(tenantId)}/members`);
  }

  async createTeam(input: { id: string; display_name: string; description?: string }): Promise<AuthTenant> {
    return this.authorized<AuthTenant>('/self-service/teams', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async addTeamMember(
    tenantId: string,
    input: { email: string; role?: 'owner' | 'admin' | 'editor' | 'viewer' },
  ): Promise<AuthTenantMember> {
    return this.authorized<AuthTenantMember>(
      `/self-service/teams/${encodeURIComponent(tenantId)}/members`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  async updateTeamMember(
    tenantId: string,
    email: string,
    role: 'owner' | 'admin' | 'editor' | 'viewer',
  ): Promise<AuthTenantMember> {
    return this.authorized<AuthTenantMember>(
      `/self-service/teams/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(email)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      },
    );
  }

  async removeTeamMember(tenantId: string, email: string): Promise<void> {
    await this.authorized<void>(
      `/self-service/teams/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    );
  }

  logout(): void {
    window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  private storeTokens(tokens: OAuthTokenResponse): void {
    if (!tokens.access_token) throw new Error('AuthAPI lieferte kein Access-Token.');
    window.sessionStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    if (tokens.refresh_token) window.sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  }

  private async authorized<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = this.accessToken;
    if (!token) throw new Error('AUTH_REQUIRED');
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(`${AUTH_API_BASE}${path}`, { ...init, headers });
    if (response.status === 401) {
      this.logout();
      throw new Error('AUTH_EXPIRED');
    }
    return readJson<T>(response);
  }
}

export const authApiClient = new AuthApiClient();

export function safeReturnPath(value: string | null | undefined): string {
  return value && /^\/(?!\/)/.test(value) && !/[\\\r\n]/.test(value) ? value : '/app';
}
